<script setup lang="ts">
  import HeartPulseIcon from '@/shared/components/icons/HeartPulseIcon.vue'
  import { useI18n } from 'vue-i18n'
  import LuggageIcon from '@/shared/components/icons/LuggageIcon.vue'
  import PlaneIcon from '@/shared/components/icons/PlaneIcon.vue'
  import { type Plan, SchemeType } from '@/shared/types/api.types'
  import { computed } from 'vue'
  import { toFixedNumber } from '@/shared/utils'
  import { useDiscount } from '@/shared/composables'
  import BaseChip from '@/shared/components/BaseChip/BaseChip.vue'

  interface Props {
    plan: Plan
  }

  const props = defineProps<Props>()

  const { t } = useI18n()

  const discountScheme = computed(() => ({
    discounts: props.plan.discounts,
    price: props.plan.price,
  }))
  const { discount } = useDiscount(discountScheme)

  const coverageOptions = [
    {
      icon: HeartPulseIcon,
      title: t('outbound.select_plan.insurance_values.category_1.label'),
      description: t('outbound.select_plan.insurance_values.category_1.quote_desc'),
    },
    {
      icon: LuggageIcon,
      title: t('outbound.select_plan.insurance_values.category_2.label'),
      description: t('outbound.select_plan.insurance_values.category_2.quote_desc'),
      notAvailableFor: [SchemeType.FlyEurope],
    },
    {
      icon: PlaneIcon,
      title: t('outbound.select_plan.insurance_values.category_3.label'),
      description: t('outbound.select_plan.insurance_values.category_3.quote_desc'),
      notAvailableFor: [SchemeType.FlyEurope],
    },
  ]

  const quotePriceWithoutDiscount = computed(() =>
    toFixedNumber(props.plan?.price + discount.value.amount)
  )
</script>

<template>
  <div class="quote-coverage">
    <div class="quote-coverage__header">
      <span>{{ $t('outbound.select_plan.coverage.label') }}</span>
      <div class="quote-coverage__prices">
        <span
          v-if="discount.amount > 0"
          class="quote-coverage__price quote-coverage__price--discount ltr"
        >
          {{ `${$t('qar')} ${toFixedNumber(quotePriceWithoutDiscount)}` }}
        </span>
        <span class="quote-coverage__price ltr">
          {{ `${$t('qar')} ${toFixedNumber(props.plan.price)}` }}
        </span>
      </div>
      <div v-if="discount.percentage" class="quote-coverage__discount-chip">
        <BaseChip v-if="discount.percentage" variant="green">
          {{ discount.name }}&nbsp;<span class="ltr">{{ `−${discount.percentage}%` }}</span>
        </BaseChip>
      </div>
    </div>
    <div class="quote-coverage__content">
      <template
        v-for="{ icon, title, description, notAvailableFor } in coverageOptions"
        :key="title"
      >
        <div v-if="!notAvailableFor?.includes(plan.schemeTypeCode)" class="quote-coverage__item">
          <component :is="icon" color="#5E597A" class="quote-coverage__icon" />
          <div class="quote-coverage__texts">
            <div class="quote-coverage__title">{{ title }}</div>
            <div class="quote-coverage__description">{{ description }}</div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
  @import '@/shared/assets/styles/mixins';

  .quote-coverage {
    display: flex;
    flex-direction: column;
    align-items: center;
    border-radius: 16px;
    border: 1px solid rgb(82 108 130 / 20%);
    background: #fff;

    &__header {
      position: relative;
      display: flex;
      flex-direction: row;
      width: 100%;
      padding: 24px;
      justify-content: space-between;
      border-bottom: 1px solid rgb(82 108 130 / 20%);

      @include caption-4-plus;

      color: #1c1c1c;

      @include mobile-flow {
        padding-inline: 16px;
      }
    }

    &__prices {
      display: flex;
      align-items: flex-end;
      gap: 8px;
    }

    &__price {
      &--discount {
        color: rgb(82 108 130 / 40%);
        text-decoration-line: line-through;

        @include caption-6;
      }
    }

    &__discount-chip {
      position: absolute;
      inset-inline-end: 24px;
      top: -12px;

      @include mobile-flow {
        inset-inline-end: 16px;
      }
    }

    &__content {
      padding: 12px 0;
    }

    &__item {
      display: flex;
      padding: 8px 24px;
      align-items: flex-start;
      gap: 12px;
      flex: 1 0 0;

      @include mobile-flow {
        padding-inline: 16px;
      }
    }

    &__icon {
      padding-top: 4px;
      flex-shrink: 0;
    }

    &__texts {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 2px;
      flex: 1 0 0;
    }

    &__title {
      color: #2e2c3a;

      @include caption-5-plus;
    }

    &__description {
      @include caption-6;

      color: #5e597a;
      line-height: 21px;
    }
  }
</style>
